import { useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  SparklesIcon,
  Loader2Icon,
  ArrowRightIcon,
} from "lucide-react";
import { toast } from "sonner";
import type { Affiliate } from "../types";

type AffiliateReferralDiscountAdjusterProps = {
  affiliate: Affiliate;
  onUpdate?: (newDiscount: number) => Promise<void>;
  compact?: boolean;
};

export const AffiliateReferralDiscountAdjuster = ({
  affiliate,
  onUpdate,
  compact = false,
}: AffiliateReferralDiscountAdjusterProps) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  
  // Max commission is the affiliate's total trading fee share
  const maxCommission = affiliate.tradingFeeShare * 100; // e.g., 40%
  const adminBaseRebate = affiliate.referralDiscount * 100; // e.g., 10% - MINIMUM set by admin
  
  const [selectedDiscount, setSelectedDiscount] = useState(adminBaseRebate);
  
  // Calculate net commission after any extra rebate given
  const extraRebateFromAffiliate = Math.max(0, selectedDiscount - adminBaseRebate);
  const affiliateNetCommission = maxCommission - extraRebateFromAffiliate;

  const handleSave = async () => {
    setIsUpdating(true);
    try {
      if (onUpdate) {
        await onUpdate(selectedDiscount / 100);
      } else {
        // Simulate API call in development
        await new Promise((resolve) => setTimeout(resolve, 500));
      }
      toast.success("Referral discount updated!");
      setModalOpen(false);
    } catch (_error) {
      toast.error("Failed to update discount. Please try again.");
    } finally {
      setIsUpdating(false);
    }
  };

  const presetOptions = [adminBaseRebate, 15, 20, 25, maxCommission].filter((p, idx, arr) => 
    p >= adminBaseRebate && p <= maxCommission && arr.indexOf(p) === idx
  );

  return (
    <>
      <div className={cn(
        "rounded-xl bg-card border border-border/50 shadow-sm dark:shadow-none",
        compact ? "p-4" : "p-4 md:p-6"
      )}>
        <div className={cn("flex items-center justify-between", compact ? "mb-3" : "mb-4")}>
          <div>
            <h3 className="text-sm font-semibold">Your Commission Split</h3>
            <p className="text-xs text-muted-foreground">Adjust what referrals get</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setModalOpen(true)}
            className="gap-1.5"
          >
            <SparklesIcon className="size-3.5" />
            Adjust
          </Button>
        </div>

        {/* Current Split Display */}
        {compact ? (
          // Compact vertical layout for 3-column grid
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-emerald-500/10 rounded-lg p-2.5 text-center border border-emerald-500/20">
                <p className="text-[10px] text-emerald-600 dark:text-emerald-400 mb-0.5">Your Net</p>
                <p className="text-xl font-bold text-emerald-500">
                  {affiliateNetCommission.toFixed(0)}%
                </p>
              </div>
              <div className="bg-primary/10 rounded-lg p-2.5 text-center border border-primary/20">
                <p className="text-[10px] text-primary mb-0.5">Referral Gets</p>
                <p className="text-xl font-bold text-primary">
                  {adminBaseRebate.toFixed(0)}%
                </p>
              </div>
            </div>
            <div className="bg-muted/30 rounded-lg p-2 text-center">
              <p className="text-[10px] text-muted-foreground">
                Base: {maxCommission.toFixed(0)}% → You keep {affiliateNetCommission.toFixed(0)}%{extraRebateFromAffiliate > 0 && ` (−${extraRebateFromAffiliate}% extra)`}
              </p>
            </div>
          </div>
        ) : (
          // Original horizontal layout
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-muted/30 rounded-lg p-3 text-center">
              <p className="text-xs text-muted-foreground mb-1">Base Commission</p>
              <p className="text-xl font-bold">{maxCommission.toFixed(0)}%</p>
              <p className="text-[10px] text-muted-foreground">on gross fees</p>
            </div>
            
            <div className="flex items-center justify-center">
              <div className="flex flex-col items-center gap-1">
                <ArrowRightIcon className="size-4 text-muted-foreground" />
                <span className="text-[10px] text-muted-foreground">split</span>
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="bg-emerald-500/10 rounded-lg p-2 text-center border border-emerald-500/20">
                <p className="text-[10px] text-emerald-600 dark:text-emerald-400 mb-0.5">Your Net</p>
                <p className="text-lg font-bold text-emerald-500">
                  {affiliateNetCommission.toFixed(0)}%
                </p>
                {extraRebateFromAffiliate > 0 && (
                  <p className="text-[9px] text-emerald-600/70 dark:text-emerald-400/70">-{extraRebateFromAffiliate}% extra</p>
                )}
              </div>
              <div className="bg-primary/10 rounded-lg p-2 text-center border border-primary/20">
                <p className="text-[10px] text-primary mb-0.5">Referral Gets</p>
                <p className="text-lg font-bold text-primary">
                  {adminBaseRebate.toFixed(0)}%
                </p>
                <p className="text-[9px] text-primary/70">platform-paid</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Adjustment Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Adjust Referral Discount
            </DialogTitle>
            <DialogDescription>
              Choose how much of your trading fee share to give to your referrals as a discount.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Visual Split */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-emerald-500/10 rounded-xl p-4 text-center border border-emerald-500/20">
                <p className="text-xs text-muted-foreground mb-1">Your Net Commission</p>
                <p className="text-3xl font-bold text-emerald-500">
                  {affiliateNetCommission.toFixed(0)}%
                </p>
                {extraRebateFromAffiliate > 0 && (
                  <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
                    ({maxCommission}% - {extraRebateFromAffiliate}% extra)
                  </p>
                )}
                {extraRebateFromAffiliate === 0 && (
                  <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
                    (full commission)
                  </p>
                )}
              </div>
              <div className="bg-primary/10 rounded-xl p-4 text-center border border-primary/20">
                <p className="text-xs text-muted-foreground mb-1">Referral Total Rebate</p>
                <p className="text-3xl font-bold text-primary">
                  {selectedDiscount.toFixed(0)}%
                </p>
                <p className="text-xs text-primary mt-1">
                  ({adminBaseRebate}% platform{extraRebateFromAffiliate > 0 ? ` + ${extraRebateFromAffiliate}% you` : ""})
                </p>
              </div>
            </div>

            {/* Slider */}
            <div className="space-y-3">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{adminBaseRebate.toFixed(0)}% (admin minimum)</span>
                <span>{maxCommission.toFixed(0)}% (give all)</span>
              </div>
              <Slider
                value={[selectedDiscount]}
                onValueChange={([value]) => setSelectedDiscount(value)}
                min={adminBaseRebate}
                max={maxCommission}
                step={1}
                className="w-full"
              />
            </div>

            {/* Preset Buttons - Styled like close position modal */}
            <div className="flex gap-2">
              {presetOptions.map((preset) => (
                <button
                  key={preset}
                  onClick={() => setSelectedDiscount(preset)}
                  className={cn(
                    "flex-1 py-1.5 text-xs font-medium rounded border transition-colors",
                    selectedDiscount === preset
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border bg-muted/30 text-muted-foreground hover:border-muted-foreground hover:text-foreground"
                  )}
                >
                  {preset}%
                </button>
              ))}
            </div>

            {/* Warning if giving away all */}
            {selectedDiscount === maxCommission && (
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  You're giving your entire commission as extra rebate. You'll earn 0% from trading fees.
                </p>
              </div>
            )}
            
            {/* Info about extra rebate */}
            {extraRebateFromAffiliate > 0 && extraRebateFromAffiliate < maxCommission && (
              <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
                <p className="text-xs text-blue-600 dark:text-blue-400">
                  Platform pays {adminBaseRebate}%, you're adding {extraRebateFromAffiliate}% from your commission.
                </p>
              </div>
            )}

            {/* Example Calculation */}
            <div className="bg-muted/30 rounded-lg p-4">
              <p className="text-xs font-medium mb-2">Example with $1,000 in fees:</p>
              <div className="space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Your earnings:</span>
                  <span className="font-bold text-emerald-500">
                    ${((1000 * affiliateNetCommission) / 100).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Platform pays rebate:</span>
                  <span className="font-medium">
                    ${((1000 * adminBaseRebate) / 100).toFixed(2)}
                  </span>
                </div>
                {extraRebateFromAffiliate > 0 && (
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">You pay extra rebate:</span>
                    <span className="font-medium text-amber-600">
                      ${((1000 * extraRebateFromAffiliate) / 100).toFixed(2)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between text-xs pt-2 border-t border-border">
                  <span className="text-muted-foreground">Referral gets total:</span>
                  <span className="font-bold text-primary">
                    ${((1000 * selectedDiscount) / 100).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={isUpdating || selectedDiscount === adminBaseRebate}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {isUpdating ? (
                <>
                  <Loader2Icon className="size-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                "Confirm"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

